// React
import {useState, useEffect} from "react";

// OpenLayers
import OlMap from "ol/Map";
import GeoJSON from "ol/format/GeoJSON";
import Feature from "ol/Feature";
import VectorLayer from "ol/layer/Vector";
import VectorSource from "ol/source/Vector";
import Geometry from "ol/geom/Geometry";
import Collection from "ol/Collection";
import BaseLayer from "ol/layer/Base";

// Models
import { Popup, PopupProps } from "lib/classes/popup";
import Address from "lib/classes/address";
import RasterImage from "lib/classes/rasterimage";
import MapToolMode from 'lib/enums/maptoolmode';

// Lang
import { getLocaleString } from "lib/localization/vendor";

// shp2geojson
import { loadshp } from "lib/includes/shp2geojson/preview";

// Tools
import { fetchRequestJson } from "lib/utilities/network";
import { makeUrl, blobToDataURL } from "lib/utilities/misc";
import { time, roundtodp } from "lib/utilities/maths";
import { 
	formatFeature, 
	getNextFeatureId, 
	featureInsersects, 
	focusFeatures 
} from "lib/utilities/feature";
import { compressImage } from "lib/utilities/image";
import { assetURL } from "lib/utilities/asset";
import { getToolEnabled } from 'lib/utilities/toolmodes';

// Styles
import "styles/css/UploadTool.scss";

/*
	Supported image formats for uploading raster images
*/
const validImageTypes:string[] = ["image/gif", "image/jpeg", "image/png", "image/tiff", "image/jpg"];

var drawingSource:VectorSource;
var onFeatureSaved:Function;
var dragCounter:number = 0;
var lastDragOver:number = 0;

const UploadToolPopup = new Popup({
	title: () => getLocaleString("uploadtool"),
	style: {width: 350},
	distinct: true,
	render: function() {
		// State hooks
		const [inputFiles, setInputFiles] = useState<FileList>();
		const [uploading, setUploading] = useState<boolean>(false);
		const [draggingOver, setDraggingOver] = useState<boolean>(false);
		
		// Map and map config
		const map:OlMap = this.get("map");
		const mapConfig:any = map.get("opus-config");
		
		const canUploadRasters:boolean = !mapConfig.isPublished || true;
		
		useEffect(() => {
			drawingSource = map.get("drawing-layer").getSource();
			onFeatureSaved = (...params:any[]) => _events.invoke("featuresaved-nosync", ...params);
			
			window.addEventListener("dragenter", onDragEnter);
			window.addEventListener("dragleave", onDragLeave);
			window.addEventListener("dragend", onDragEnd);
			window.addEventListener("drop", onDrop);
			
			// this.onClose applies to this popup instance
			this.onClose = () => {
				window.removeEventListener("dragenter", onDragEnter);
				window.removeEventListener("dragleave", onDragLeave);
				window.removeEventListener("dragend", onDragEnd);
				window.removeEventListener("drop", onDrop);
			}
			
			dragCounter = 0;
		}, []);
		
		const onDragEnter = (e:DragEvent) => {
			e.preventDefault();
			setDraggingOver(true);
			//console.log("Dragging over!");
			dragCounter++;
			lastDragOver = time();
		}
		const onDragLeave = (e:DragEvent) => {
			e.preventDefault();
			
			dragCounter--;
			
			if(dragCounter <= 0 && time() - lastDragOver > 0.1)
				setDraggingOver(false);
		}
		const onDragEnd = (e:DragEvent) => {
			e.preventDefault();
			setDraggingOver(false);
			
			//console.log("END");
		}
		const onDrop = (e:DragEvent) => {
			e.preventDefault();
			
			const files:FileList = e.dataTransfer.files;
			
			setDraggingOver(false);
			uploadFiles(files);
			
			//console.log("DROP");
		}
		const drawDragBox = () => {
			if(!draggingOver) return;
			
			return (
				<div className="drag-file-box">
					<div>
						<img src={assetURL("assets/images/dragdrop.png")} />
						<h2>{getLocaleString("uploadfiles")}</h2>
					</div>
				</div>
			)
		}
		const uploadFiles = (files:FileList) => {
			setUploading(true);
			
			//console.log(files);
			
			for(let i = 0; i < files.length; i++) {
				const file:File = files[i];
				
				let processedFile = false;
				
				/*
					Check if file is an image for raster image upload
				*/
				if(validImageTypes.includes(file.type)) {
					if(canUploadRasters) {
						uploadRasterImage(file);
					}else{
						// Public maps do not allow this feature
						_ui.showError(getLocaleString("nouploadraster"));
						setUploading(false);
					}
					return;
				}
				
				/*
					Unzip and read shape files
				*/
				loadshp({ url: file, encoding: "utf-8", EPSG: 3857 }, (result:any) => {
					if(processedFile) {
						console.log("Ignoring re-call bug");
						return;
					}
					processedFile = true;
					if(result === false) {
						_ui.showError(getLocaleString("uploaderror", ["invalid file"]), getLocaleString("invalidsf"));
						setUploading(false);
						return;
					}
					
					const geojson:any = result;
					
					try {
						// Convert GeoJSON features into Feature<Geometry>[]
						const features:Feature<Geometry>[] = new GeoJSON().readFeatures(geojson, {
							featureProjection: map.getView().getProjection(),
						});
						
						// Array of features that intersect with other features
						const intersections:Feature<Geometry>[] = [];
							
						// Array of features that were too big/small
						const featuresTooSmall:Feature<Geometry>[] = [];
						const featuresTooBig:Feature<Geometry>[] = [];
							
						// Array of features that are actually added to the source
						const featuresAdded:Feature<Geometry>[] = [];
						
						features.forEach((feature:Feature<Geometry>) => {
							const featureType:string = feature.getGeometry().getType();
							
							feature.setId(getNextFeatureId(drawingSource));
							feature.set("origin", "uploaded"); // This feature was uploaded
							
							try {
								// Format the feature according to the tools/Feature methods
								formatFeature(feature);
							}catch(e:any) {
								console.log("Skipping 'corrupt' feature.");
								return;
							}
							
							// Get feature that intersects with this one
							const featureConflict:Feature<Geometry> = featureInsersects(feature, drawingSource, featureType);
							
							if(featureConflict) {
								if(intersections.indexOf(featureConflict) < 0)
									intersections.push(featureConflict);
								
								// Still insert feature but allow error
								//return;
							}
							
							if(feature.get("area") < _config.minimumPolygonArea && featureType == "Polygon" && feature.get("area")) {
								featuresTooSmall.push(feature);
							}
							if(feature.get("area") > _config.maximumPolygonArea && featureType == "Polygon" && feature.get("area")) {
								featuresTooBig.push(feature);
							}
							if(feature.get("area-m2") < _config.minimumPolygonArea && featureType == "Polygon" && feature.get("area-m2")) {
								featuresTooSmall.push(feature);
							}
							if(feature.get("area-m2") > _config.maximumPolygonArea && featureType == "Polygon" && feature.get("area-m2")) {
								featuresTooBig.push(feature);
							}
							
							drawingSource.addFeature(feature);
							onFeatureSaved(feature);
							
							featuresAdded.push(feature);
						});
						
						// Focus map on the features that were added
						focusFeatures(map, featuresAdded);
						
						if(intersections.length > 0 || featuresTooSmall.length > 0 || featuresTooBig.length > 0) {
							_ui.showError(getLocaleString("uploadclean", [
								featuresTooSmall.length.toString(), 
								featuresTooBig.length.toString(),
								intersections.length.toString(), 
								featuresAdded.length.toString()
							]), getLocaleString("warning"));
						}
							
						_ui.closePopupOf(this);
						_events.invoke('sync-drawtool'); // DrawTool will sync the features with server
					}catch(e:any) {
						console.log(e);
						_ui.showError(getLocaleString("uploaderror", [e]), getLocaleString("invalidsf"));
					}
					
					setUploading(false);
				});
			}
		}
		const onChangeFileInput = (e:any) => {
			setInputFiles(e.target.files);
		}
		const uploadFileInput = () => {
			if(!inputFiles) 
				return _ui.showError(getLocaleString("nofiles"));
			
			uploadFiles(inputFiles);
		}
		const uploadRasterImage = (file:File) => {
			const sizeLimitMb:number = 3;
			
			const onBlobReady = (blob:File) => {
				// Create a data URL from this blob
				blobToDataURL(blob)
				.then((base64:string) => {
					const image:any = new Image();
					
					image.onload = () => {
						// Call uploaded event with data url and image passed
						_events.invoke("raster-image-uploaded", base64, image);
						
						// Set uploading state to false
						setUploading(false);
						
						// Close upload popup when done
						_ui.closePopupOf(this);
					}
					
					// Load blob into image so dimensions can be read
					image.src = base64;
				})
				.catch((error:Error) => _ui.showError(error.message));
			}
			
			if(file.size > sizeLimitMb * 1024 * 1024) {
				_ui.showLoading(getLocaleString("compressingimg"));
				
				compressImage(file, sizeLimitMb)
				.then((blob:File) => {
					const reduction:number = 100 - Math.floor(blob.size / file.size * 100);
					const initialMb:number = roundtodp(file.size / 1024 / 1024, 2);
					const newMb:number = roundtodp(blob.size / 1024 / 1024, 2);
					
					_ui.showNotice(getLocaleString("compressnotice", [
						String(reduction), String(initialMb), String(newMb)
					]));
					
					_ui.hideLoading();
					
					uploadRasterImage(blob);
				})
				.catch((error:Error) => _ui.showError(error.message));
			}else{
				onBlobReady(file);
			}
		}
		
		return (
			<>
				{drawDragBox()}
				
				<div style={{paddingBottom: 10, fontSize: 14, lineHeight: 1}}>
					<p><b>{getLocaleString("uploadtip")}</b></p>
					<p>• {getLocaleString("uploadformat1")}</p>
					{canUploadRasters ? (<p>• {getLocaleString("uploadformat2")}</p>) : null}
				</div>
				<div className="upload-form">
					<input 
						type="file" 
						accept=".zip,.png,.jpeg,.jpg,.gif" 
						onChange={onChangeFileInput} 
					/>
					
					<button type="submit" onClick={uploadFileInput} disabled={uploading}>
						{!uploading ? getLocaleString("upload") : `${getLocaleString("uploading")}...`}
					</button>
				</div>
			</>
		)
	}
});

export default UploadToolPopup;